/**
 * The authenticated write API — same origin as the site (CloudFront proxies
 * `/api/*` to API Gateway, see `infra/api.tf`), so no base-URL env var is
 * needed the way `VITE_DATA_BASE` is for reads.
 */

import type {
  ManualTeamInput,
  ManualWeekResult,
  MatchupDraft,
  MatchupReport,
  MatchupReportIndex,
  PayoutStructure,
  PayoutWinner,
  SeasonArchive,
  SeasonDues,
  SeasonManagerAliases,
  SeasonPayoutWinners,
  TeamDues,
  TeamManagerAlias,
} from "@averagejoesff/shared";

async function request<T>(
  method: "GET" | "POST" | "PUT" | "DELETE",
  path: string,
  idToken: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${idToken}`,
    },
    ...(body !== undefined && { body: JSON.stringify(body) }),
  });

  if (!res.ok) {
    const detail = await res.json().catch(() => null);
    throw new Error(
      (detail && typeof detail === "object" && "error" in detail && String(detail.error)) ||
        `Request failed (${res.status} ${res.statusText})`,
    );
  }

  return (await res.json()) as T;
}

function putJson<T>(path: string, body: unknown, idToken: string): Promise<T> {
  return request("PUT", path, idToken, body);
}

export function upsertWeek(
  season: number,
  week: number,
  result: Omit<ManualWeekResult, "week">,
  idToken: string,
): Promise<SeasonArchive> {
  return putJson(`/api/seasons/${season}/weeks/${week}`, result, idToken);
}

export function upsertTeams(
  season: number,
  teams: ManualTeamInput[],
  idToken: string,
): Promise<SeasonArchive> {
  return putJson(`/api/seasons/${season}/teams`, { teams }, idToken);
}

/** Wipes every entered week's result for a season; leaves the roster alone. */
export function clearResults(season: number, idToken: string): Promise<SeasonArchive> {
  return request("DELETE", `/api/seasons/${season}/results`, idToken);
}

export function upsertPayouts(
  payouts: Omit<PayoutStructure, "updatedAt">,
  idToken: string,
): Promise<PayoutStructure> {
  return putJson("/api/payouts", payouts, idToken);
}

export function upsertDues(
  season: number,
  teams: TeamDues[],
  idToken: string,
): Promise<SeasonDues> {
  return putJson(`/api/seasons/${season}/dues`, { teams }, idToken);
}

export function upsertManagerAliases(
  season: number,
  teams: TeamManagerAlias[],
  idToken: string,
): Promise<SeasonManagerAliases> {
  return putJson(`/api/seasons/${season}/manager-aliases`, { teams }, idToken);
}

export function upsertPayoutWinners(
  season: number,
  winners: PayoutWinner[],
  idToken: string,
): Promise<SeasonPayoutWinners> {
  return putJson(`/api/seasons/${season}/payout-winners`, { winners }, idToken);
}

/** Publishes (or replaces) the report for its own season/week; returns it plus the updated index. */
export function publishMatchupReport(
  report: MatchupReport,
  idToken: string,
): Promise<{ report: MatchupReport; index: MatchupReportIndex }> {
  return putJson(`/api/matchups/${report.season}/weeks/${report.week}`, report, idToken);
}

/** The fetcher's latest generated report awaiting review, or null. Signed-in only. */
export async function fetchMatchupDraft(idToken: string): Promise<MatchupDraft | null> {
  const { draft } = await request<{ draft: MatchupDraft | null }>("GET", "/api/matchups/draft", idToken);
  return draft;
}

/** Publishes the pending draft; returns the published report plus the updated index. */
export function publishMatchupDraft(
  idToken: string,
): Promise<{ report: MatchupReport; index: MatchupReportIndex }> {
  return request("POST", "/api/matchups/draft/publish", idToken);
}
